// frontend/src/pages/BattleDetailsPage.js
// Detailed view of a specific boss battle league

import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../auth/AuthContext';
import LoadoutSetup from '../components/bossBattles/LoadoutSetup';
import BattleResults from '../components/bossBattles/BattleResults';
import {
    fetchBossLeague,
    fetchLeagueParticipants,
    fetchBattleLog,
    fetchBossHeads,
    setParticipantLoadout,
    confirmLoadout,
    claimReward,
    isBossBattlesFeatureDisabled
} from '../api/bossBattlesApi';
import BossBattlesUnavailable from './BossBattlesUnavailable';
import { Button } from '../components/ui';
import './BattleDetailsPage.css';

function BattleDetailsPage({ setPage }) {
    const { user, isAuthenticated } = useContext(AuthContext);
    const id = sessionStorage.getItem('selectedBossLeagueId');
    
    const [league, setLeague] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [userParticipant, setUserParticipant] = useState(null);
    const [battleLog, setBattleLog] = useState(null);
    const [bossHeads, setBossHeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Server's authoritative rollout refusal — see BossBattlesPage for the full
    // reasoning. Kept separate from `error` so a switched-off mode renders the
    // unavailable screen, not the error panel.
    const [featureDisabled, setFeatureDisabled] = useState(false);
    const [activeTab, setActiveTab] = useState('loadout'); // 'loadout' or 'results'
    
    // Mock data for player/gear selection (in real app, fetch from API)
    const [availablePlayers] = useState([]);
    const [availableGear] = useState([]);
    
    // Load battle data on mount
    useEffect(() => {
        if (id) {
            loadBattleData();
        }
    }, [id]);
    
    const loadBattleData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log(`BattleDetailsPage: Loading battle data for league ${id}`);
            
            // Fetch league details
            const leagueData = await fetchBossLeague(id);
            console.log('BattleDetailsPage: League data:', leagueData);
            
            // VALIDATE DATA - ERROR LOUDLY IF NOT AS EXPECTED
            if (!leagueData || !leagueData.id) {
                throw new Error('❌ INVALID DATA: Expected league object with id');
            }
            
            setLeague(leagueData);
            
            // Fetch participants
            const participantsData = await fetchLeagueParticipants(id);
            console.log('BattleDetailsPage: Participants data:', participantsData);
            
            if (!Array.isArray(participantsData)) {
                throw new Error('❌ INVALID DATA: Expected array of participants');
            }
            
            setParticipants(participantsData);
            
            // Find current user's participation
            const userPart = participantsData.find(p => p.user === user?.id);
            if (userPart) {
                setUserParticipant(userPart);
                console.log('BattleDetailsPage: User participation found:', userPart);
            } else {
                console.log('BattleDetailsPage: User has not joined this battle');
            }
            
            // If battle is resolved, fetch battle log and heads
            if (leagueData.status === 'RESOLVED' || leagueData.status === 'COMPLETED') {
                try {
                    const logData = await fetchBattleLog(id);
                    setBattleLog(logData);
                    setActiveTab('results'); // Show results by default for resolved battles
                } catch (logError) {
                    console.log('BattleDetailsPage: Battle log not available yet');
                }
                
                try {
                    const headsData = await fetchBossHeads(id);
                    setBossHeads(headsData);
                } catch (headsError) {
                    console.log('BattleDetailsPage: Boss heads not available');
                }
            }
            
        } catch (err) {
            if (isBossBattlesFeatureDisabled(err)) {
                console.log('🔒 BattleDetailsPage: the mode_boss_battle gate refused this user — showing the unavailable screen');
                setFeatureDisabled(true);
                return;
            }
            console.error('❌ BattleDetailsPage: Failed to load battle data:', err);
            setError(err.message || 'Failed to load battle details');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSaveLoadout = async (loadoutData) => {
        if (!userParticipant) {
            throw new Error('You must join this battle first');
        }
        
        try {
            console.log('BattleDetailsPage: Saving loadout:', loadoutData);
            
            const updatedParticipant = await setParticipantLoadout(userParticipant.id, loadoutData);
            
            console.log('✅ BattleDetailsPage: Loadout saved:', updatedParticipant);
            
            // Update local state
            setUserParticipant(updatedParticipant);
            
            // Refresh participant list
            const participantsData = await fetchLeagueParticipants(id);
            setParticipants(participantsData);
            
        } catch (error) {
            console.error('❌ BattleDetailsPage: Failed to save loadout:', error);
            throw error;
        }
    };
    
    const handleConfirmLoadout = async () => {
        if (!userParticipant) {
            throw new Error('You must join this battle first');
        }
        
        try {
            console.log('BattleDetailsPage: Confirming loadout');
            
            const updatedParticipant = await confirmLoadout(userParticipant.id);
            
            console.log('✅ BattleDetailsPage: Loadout confirmed:', updatedParticipant);
            
            // Update local state
            setUserParticipant(updatedParticipant);
            
        } catch (error) {
            console.error('❌ BattleDetailsPage: Failed to confirm loadout:', error);
            throw error;
        }
    };
    
    const handleClaimReward = async () => {
        if (!userParticipant) {
            throw new Error('You must be a participant to claim rewards');
        }
        
        try {
            console.log('BattleDetailsPage: Claiming reward');
            
            const result = await claimReward(userParticipant.id);
            
            console.log('✅ BattleDetailsPage: Reward claimed:', result);
            
            // Update local state
            if (result.participant) {
                setUserParticipant(result.participant);
            }
            
            alert(`Successfully claimed ${result.mythril_rewarded} Mythril!`);
            
        } catch (error) {
            console.error('❌ BattleDetailsPage: Failed to claim reward:', error);
            alert(`Failed to claim reward: ${error.message}`);
        }
    };
    
    const handleBack = () => {
        sessionStorage.removeItem('selectedBossLeagueId');
        if (setPage) {
            setPage('bossBattlesPage');
        }
    };

    // Checked before `loading`/`error`. Back goes to My Teams rather than the
    // battle list, because that list is gated by the same flag and would just
    // bounce the user straight back to this screen.
    if (featureDisabled) {
        return (
            <BossBattlesUnavailable
                onBack={() => {
                    sessionStorage.removeItem('selectedBossLeagueId');
                    if (setPage) {
                        setPage('myTeams');
                    }
                }}
            />
        );
    }

    if (loading) {
        return (
            <div className="battle-details-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading battle details...</p>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="battle-details-page">
                <div className="error-container">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <Button className="back-button" onClick={handleBack} variant="secondary">
                        ← Back to Battles
                    </Button>
                </div>
            </div>
        );
    }
    
    if (!league) {
        return (
            <div className="battle-details-page">
                <div className="error-container">
                    <h2>Battle Not Found</h2>
                    <Button className="back-button" onClick={handleBack} variant="secondary">
                        ← Back to Battles
                    </Button>
                </div>
            </div>
        );
    }
    
    const isResolved = league.status === 'RESOLVED' || league.status === 'COMPLETED';
    const isLocked = league.status !== 'RECRUITING';
    const showLoadoutTab = userParticipant && (!isResolved || activeTab === 'loadout');
    const showResultsTab = isResolved && battleLog;
    
    return (
        <div className="battle-details-page">
            {/* Header */}
            <div className="battle-header">
                <Button className="back-button" onClick={handleBack} variant="secondary">
                    ← Back
                </Button>
                <h1 className="battle-title">{league.boss_name}</h1>
                <p className="battle-description">{league.description}</p>
                <div className="battle-status-bar">
                    <span className="status-badge" style={{
                        backgroundColor: league.status === 'RECRUITING' ? '#4a90e2' :
                                       league.status === 'LOCKED' ? '#e2a34a' : '#4ae28c'
                    }}>
                        {league.status}
                    </span>
                    <span className="participant-count">
                        {league.current_participants || 0}/{league.max_participants} Warriors
                    </span>
                </div>
            </div>
            
            {/* No Participation Warning */}
            {!userParticipant && (
                <div className="warning-banner">
                    ⚠️ You have not joined this battle.
                    <Button onClick={handleBack} className="join-link" variant="secondary">Join Now</Button>
                </div>
            )}
            
            {/* Tab Navigation */}
            {(showLoadoutTab || showResultsTab) && (
                <div className="tab-navigation">
                    {showLoadoutTab && (
                        <Button
                            className={`tab-button ${activeTab === 'loadout' ? 'active' : ''}`}
                            onClick={() => setActiveTab('loadout')}
                            variant="secondary"
                        >
                            ⚔️ Loadout
                        </Button>
                    )}
                    {showResultsTab && (
                        <Button
                            className={`tab-button ${activeTab === 'results' ? 'active' : ''}`}
                            onClick={() => setActiveTab('results')}
                            variant="secondary"
                        >
                            🏆 Results
                        </Button>
                    )}
                </div>
            )}
            
            {/* Content */}
            <div className="battle-content">
                {activeTab === 'loadout' && userParticipant && (
                    <LoadoutSetup
                        participant={userParticipant}
                        availablePlayers={availablePlayers}
                        availableGear={availableGear}
                        onSaveLoadout={handleSaveLoadout}
                        onConfirmLoadout={handleConfirmLoadout}
                        isLocked={isLocked}
                    />
                )}
                
                {activeTab === 'results' && battleLog && (
                    <BattleResults
                        battleLog={battleLog}
                        participants={participants}
                        bossHeads={bossHeads}
                        onClaimReward={handleClaimReward}
                        userParticipant={userParticipant}
                    />
                )}
                
                {/* Participants List (always show) */}
                <div className="participants-section">
                    <h3 className="section-title">Battle Roster</h3>
                    {participants.length === 0 ? (
                        <div className="empty-message">No warriors have joined yet</div>
                    ) : (
                        <div className="roster-list">
                            {participants.map(participant => (
                                <div 
                                    key={participant.id}
                                    className={`roster-item ${userParticipant?.id === participant.id ? 'current-user' : ''}`}
                                >
                                    <span className="warrior-name">
                                        {participant.user_display_name || `Warrior ${participant.id}`}
                                    </span>
                                    <span className="warrior-role">{participant.role}</span>
                                    {participant.loadout_confirmed && (
                                        <span className="ready-badge">✓ Ready</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BattleDetailsPage;
